import classNames from "classnames"
import { useEffect, useRef, useState } from "react"
import { Button, Tooltip } from "reactstrap"

interface IProps {
  children?: React.ReactNode
  additionalClassName?: string
  disabled?: boolean
  isActive: boolean
  isImage?: boolean
  onClick: any
  tooltipText?: string
  /** id for testing:
   * Because the "data-testid" is not able to be tied onto the component itself,
   * it is passed through to the inside of the component where it is tied to
   * the <button>-tag.
   */
  "data-testid"?: string
}

/**
 * This components provides a single item of a set of categories as an clickable button.
 * The button's content is provided as a child and may be an image or text.
 */
const ProjectCategoryFilterItem: React.FC<IProps> = (props: IProps) => {
  const { children, additionalClassName = "", onClick, isActive, disabled, isImage = false, tooltipText } = props
  const className = classNames(
    { "image-filteritem": isImage },
    { "text-filteritem": !isImage },
    { "image-filteritem--active": isImage && isActive },
    { "text-filteritem--active": !isImage && isActive },
    additionalClassName
  )

  const [tooltipOpen, setTooltipOpen] = useState(false)
  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen)
  }


  // Hint of the official documentation: https://reactjs.org/docs/hooks-reference.html#useref
  /**
   * Keep in mind that useRef doesn’t notify you when its content changes.
   * Mutating the .current property doesn’t cause a re-render.
   * If you want to run some code when React attaches or detaches a ref to a DOM node,
   * you may want to use a callback ref instead.
   */
  // e.g the tooltip should be display when the targets are initialized, that mean's if the target reference is initialized
  // if the tooltipTargetRef is still null, the tooltip will not be rendered
  // Be aware: the tooltipTargetRef can be intialized before the first render, but there is no guarante
  //
  // solution: adding a useState und a useEffect, which has the tooltipTargetRef in the dependency array
  // the callback idea is not suitable in this case with making sure to display the tooltip, cause with the callback, there is e.g. the possiblity to do e.g. manipulation on the target or focus,
  // but to display the tooltip, a rerender is needed, therefor a useState and a useEffect is the suitable solution
  const [targetRefInitialized, setTargetRefInitialized] = useState(false)
  // reference to to the tooltip target, initially null => needed to make sure, that the tooltip is rendered after the tooltip targets are mounted
  const tooltipTargetRef = useRef(null)

  useEffect(() => {
    if (tooltipTargetRef?.current) {
      setTargetRefInitialized(true)
    }
  }, [tooltipTargetRef])



  return <>
    <span
      ref={tooltipTargetRef}
    >
      <Button
        className={className}
        onClick={onClick}
        disabled={disabled}
        data-testid={props["data-testid"]}
      >
        {children}
      </Button>
    </span>
    {/* if a tooltiptext is not empty and if the tooltip target is only mounted, render the the tooltip */}
    {tooltipText && tooltipText !== "" && targetRefInitialized && tooltipTargetRef?.current && (
      <Tooltip
        isOpen={tooltipOpen}
        target={tooltipTargetRef.current}
        toggle={toggleTooltip}
      >
        {tooltipText}
      </Tooltip>
    )}
  </>
}

export default ProjectCategoryFilterItem
